import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteTypes } from '~/routes/constants';
import { Accordion, Grid, Icon, Popup, Tab, TabPane, Transition } from 'semantic-ui-react';
import Layout from '~/components/Layout';
import notify from '~/components/Notify';
import NewsletterService from '~/api/services/newsletter';
import {
  RecipientsAccordion,
  RecipientsAccordionTitle
} from '../style';
import i18n from '~/components/i18n';
import { NewsletterQuery } from '~/QueryHooks/NewsLetterQuery';
import { useParams } from 'react-router-dom';
import ShowInfo from '../ShowNewsletterDairy/ShowInfo';
import { ShowGridRow } from '../ShowNewsletterDairy/style';
import ShowAttachments from './ShowAttachments';
import TableComponent from '~/components/TableComponent';
import CustomIcon from '~/components/CustomIcon';

const getTableColumns = () => [
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo'
  },
  {
    name: i18n.t('Name'),
    key: 'name'
  },
  {
    name: i18n.t('Email'),
    key: 'email'
  },
  {
    name: i18n.t('Status'),
    key: 'opened',
    type: 'percent-status-news'
  },
  {
    name: i18n.t('Opened via web/mobile app'),
    key: 'openedInApp',
    type: 'verified'
  }
];

const ShowNewsletterDairy: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [isBusy, setIsBusy] = useState(false);
  const [showData, setShowData] = useState(null);
  const [activeAccordionIndex, setActiveAccordionIndex] = useState(-1);
  const [activeIndex, setActiveIndex] = useState<string | number>(0);

  const {
    data: newsletterRecipients,
    isLoading: recipientsDataLoading
  } = NewsletterQuery.getNewsletterRecipientsById(id);

  useEffect(() => {
    if (id) {
      fetchShowData();
    }
  }, []);

  const fetchShowData = async () => {
    setIsBusy(true);
    try {
      const showResponse = await NewsletterService.showNewsletter(id);
      setShowData(showResponse.data);
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false);
  };

  const handleRecipientsHeaderClick = (e, titleProps) => {
    const { index } = titleProps;
    setActiveAccordionIndex(activeAccordionIndex === index ? -1 : index);
  };

  function renderStatusNewsletter(value) {
    const iconName = value?.openedAt || value?.openedInApp ? 'envelope open' : 'envelope'
    const content = value?.openedAt || value?.openedInApp ? t('Read') : t('Not open')
    return <Popup
      content={content}
      trigger={<CustomIcon className={iconName} style={{ fontSize: '1.2em' }} />}
    />
  }

  function renderColumn(type, value) {
    switch (type) {
      case 'percent-status-news':
        return renderStatusNewsletter(value)
    }
  }

  const renderRecipientsTable = () => {
    return <TableComponent
      tableColumnHeaders={getTableColumns()}
      rows={newsletterRecipients || []}
      total={newsletterRecipients?.length || 0}
      removePagination
      renderColumn={(type, _, row) => renderColumn(type, row)}
    />
  };

  const renderLanguagePane = (language, languageIndex) => {
    return (
      <TabPane
        attached={false}
        active={languageIndex === activeIndex}
        key={languageIndex}
      >
        <Grid columns={1} stackable>
          <Grid.Column>
            <ShowGridRow>
              <Grid.Row>
                <strong>{t('Subject')}</strong>
              </Grid.Row>
              <Grid.Row>
                {language?.subject}
              </Grid.Row>
            </ShowGridRow>

            <ShowGridRow>
              <Grid.Row>
                <strong>{t('Content')}</strong>
              </Grid.Row>
              <div dangerouslySetInnerHTML={{ __html: language?.content }} />
            </ShowGridRow>
            {language.attachments && language.attachments.length > 0 && <Grid.Row>
              <strong>{t('Attachment')}</strong>
              <ShowAttachments attachments={language.attachments} />
            </Grid.Row>
            }
          </Grid.Column>
        </Grid>
      </TabPane>
    );
  };

  const languagePanes = () => {
    return showData?.languages?.map((language, languageIndex) => {
      return {
        menuItem: language?.label || null,
        render: () => renderLanguagePane(language, languageIndex)
      };
    });
  };

  const renderLanguageTabs = () => {
    return (
      <Tab menu={{ secondary: true, pointing: true }}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
        panes={languagePanes()} />
    );
  };

  return <Layout
    isBusy={isBusy}
    segmented
    title={t('Newsletter preview')}
    close={RouteTypes.NEWSLETTER}
  >
    <>
      <ShowInfo showData={showData} />
      {renderLanguageTabs()}
      <RecipientsAccordion styled>
        <RecipientsAccordionTitle
          onClick={handleRecipientsHeaderClick}
          index={0}
          active={activeAccordionIndex === 0}>
          <div>{t('Recipients')}</div>
          <Icon name={activeAccordionIndex === 0 ? 'minus' : 'plus'} />
        </RecipientsAccordionTitle>
        <Transition visible={activeAccordionIndex === 0} animation='slide down' duration={200}>
          <Accordion.Content active={activeAccordionIndex === 0} content={renderRecipientsTable()} />
        </Transition>
      </RecipientsAccordion>
    </>

  </Layout>;
};
export default ShowNewsletterDairy;
